<template>
  <VDataGridV2
      :loader="dataLoader"
      :context-menu="PreordersService.contextMenu"
      sticky
      paginated
      entity-type="preorderPosition"
      @row:dblclick="openPositionCard"
  >
    <VDataGridColumnV2
        v-if="isColumnVisible('comment')"
        id="comment"
        content-class="comment"
    >
      <template #default="{ row }">
        <CommentsButton entity-type-name="preorderPosition" :entity="row" />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('id')"
        id="id"
        width="100px"
        sortable
        sort-default-value="desc"
        :filter="$core.filters.idPattern"
    >
      <template #label>
        №
        <div class="description">Дата</div>
      </template>
      <template #default="{ row }">
        <VLink @click.stop="openPositionCard(row)">
          {{ $core.utils.string.formatWithDelimiter(row.id) }}
        </VLink>
        <div class="description">
          от {{ $core.utils.date.format(row.createdAt, 'DD.MM.yyyy') }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="showSellerColumn && isColumnVisible('seller')"
        width="150px"
        content-class="ellipsis"
        :filter="$core.filters.seller"
        filter-by-value
    >
      <template #label>
        Грузоотправитель
        <div class="description">Юр лицо</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.seller.name">
          {{ row.seller.name }}
        </div>
        <div class="description ellipsis" :title="row.sellerCompany.name">
          {{ row.sellerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="showBuyerColumn && isColumnVisible('buyer')"
        width="150px"
        content-class="ellipsis"
        :filter="$core.filters.buyer"
    >
      <template #label>
        Грузополучатель
        <div class="description">Юр лицо</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.buyer.name">
          {{ row.buyer.name }}
        </div>
        <div class="description ellipsis" :title="row.buyerCompany.name">
          {{ row.buyerCompany.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('userCode')"
        id="userCode"
        label="Арт"
        width="90px"
        content-align="center"
        content-class="ellipsis"
        filterable
        filter-send-name="codePattern"
        :filter-props="{ placeholder: 'Артикул', clearable: true }"
    >
      <template #default="{ row }">
        <div class="ellipsis" :title="row.code">{{ row.code || '--' }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('product')"
        id="product"
        width="250px"
        content-class="ellipsis"
        filterable
        filter-send-name="namePattern"
        :filter-props="{ placeholder: 'Название товара', clearable: true }"
    >
      <template #label>
        Товар
      </template>
      <template #default="{ row }">
        {{ row.name }}
        <VBadges>
          <VBadge v-if="!row.isOfferBased" class="primary">Прямой</VBadge>
          <VBadge v-if="row.isSamples">Образцы</VBadge>
          <VBadge
              v-if="row.isQuote"
              class="default ellipsis"
              @mouseenter="$core.tooltip.show($event.currentTarget, 'Свободный выкуп')"
              @mouseleave="$core.tooltip.hide()"
          >
            Свободный выкуп
          </VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('qty')"
        id="qty"
        width="140px"
        content-align="right"
    >
      <template #label>
        Доступно
        <div class="description">Всего</div>
        <div class="description nowrap">Страх. запас</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">{{ row.qtyLeft.format(row.unit) }}</div>
        <div class="description nowrap">{{ row.qty.format(row.unit) }}</div>
        <div class="description nowrap">
          <template v-if="row.preorderMonthlyReserve">
            {{ row.preorderMonthlyReserve.format(row.unit) }}
          </template>
          <template v-else>
            Нет
          </template>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('sum') && canViewSellerData"
        id="sumSeller"
        width="140px"
        content-align="right"
    >
      <template #label>
        Цена
        <div class="description">Сумма</div>
      </template>
      <template #default="{ row }">
        {{ row.priceFullSeller.format(row.currency, row.unit) }}
        <div class="description">{{ row.sumFullSeller.format(row.currency) }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('sum') && canViewBuyerData"
        id="sumBuyer"
        width="140px"
        content-align="right"
    >
      <template #label>
        Цена покупателя
        <div class="description">Сумма</div>
      </template>
      <template #default="{ row }">
        {{ row.priceFullBuyer?.format(row.currency, row.unit) ?? '--' }}
        <div class="description">{{ row.sumFullBuyer?.format(row.currency) ?? '--' }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('manufacturer')"
        id="manufacturer"
        content-class="ellipsis"
        width="135px"
    >
      <template #label>
        Производитель
        <div class="description">Годен до</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis">{{ row.manufacturer.name }}</div>
        <div v-if="row.productExpirationDate" class="description nowrap">
          Годен до {{ $core.utils.date.format(row.productExpirationDate, 'DD.MM.yyyy') }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('preorderShipmentPeriod')"
        id="preorderShipmentPeriod"
        width="210px"
        content-class="ellipsis"
        sortable
        :filter="$core.filters.period('preorderShipmentPeriod')"
    >
      <template #label>
        <div class="nowrap">Период отгрузки</div>
        <div class="nowrap description">Тип доставки</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">{{ row.preorderShipmentPeriod.toString({ prefix: 'с', middle: 'по' }) }}</div>
        <div class="description ellipsis">{{ row.deliveryType.name }}</div>
        <VPreorderStoresPopup
            :seller-store="row.sellerStore"
            :buyer-store="row.buyerStore"
            :delivery-days="row.deliveryDays"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('storeTemperatureRange')"
        id="storeTemperatureRange"
        label="Темп. режим"
        width="145px"
    >
      <template #default="{ row }">
        {{ row.storeTemperatureRange?.toString({ unit: '°C', negative: 'без температурных ограничений' }) }}
        <div v-if="row.storeRulesText" class="description">
          {{ row.storeRulesText }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="isColumnVisible('status')"
        id="status"
        label="Статус"
        width="88px"
        content-align="center"
        filterable
        filter-type="select"
        filter-identity="isFinished,isDeleted"
        :filter-props="{ options: PreordersService.States, clearable: true }"
    >
      <template #default="{ row }">
        {{ row.statusName }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        v-if="$core.currentPartner.isSeller === false && isColumnVisible('actions')"
        id="actions"
        width="32px"
        content-align="center"
        content-class="form"
    >
      <template #default="{ row }">
        <VButton
            :disabled="row.qtyLeft.isPositive() === false"
            @click.stop="openBuyPopup($event, row)"
        >
          <template #icon>
            <VIcon material="add_shopping_cart" />
          </template>
        </VButton>
      </template>
    </VDataGridColumnV2>

    <!-- Заказы и предзаказы оформляет покупатель — продавцу панель ни к чему -->
    <VDataGridActionBarV2 v-if="showActionBar && $core.currentPartner.isSeller === false">
      <VButton @click="createPrintingPreorder">
        Создать предзаказ полиграфии
      </VButton>
      <VButton @click="createRawMaterialsPreorder">
        Создать предзаказ
      </VButton>
    </VDataGridActionBarV2>
  </VDataGridV2>

  <BuyOneClickPopup
    :visible="popup.target !== null"
    :entity="popup.entity"
    :target="popup.target"
    :is-save-disabled="popup.isSaveDisabled"
    :is-loading="popup.isLoading"
    @close="closeBuyPopup"
    @start-create-order="popup.isSaveDisabled = true"
    @cancel="popup.isSaveDisabled = false"
    @create-order="createOrder"
  />
</template>

<script lang="ts">
import OrdersService        from '@/domains/Orders/OrdersService';
import PreordersService     from '@/domains/Preorders/PreordersService';
import PreorderPosition     from '@/domains/Preorders/PreorderPosition';
import DataGridLoader       from '@/lib/components/VDataGridV2/DataGridLoader';
import VPreorderStoresPopup from '@/components/VPreorderStoresPopup/VPreorderStoresPopup.vue';
import BuyOneClickPopup from "@/domains/Preorders/components/BuyOneClickPopup/BuyOneClickPopup.vue";
import Order from "@/domains/Orders/Order";
import type OrdersPageFilter from '@/domains/Orders/components/OrdersPage/OrdersPageFilter/OrdersPageFilter';
import type { UnwrapNestedRefs } from 'vue';

export default defineComponent({
  name: 'PreordersRegistry',

  components: {
    VPreorderStoresPopup,
    BuyOneClickPopup
  },

  props: {
    /**
     * Админский режим — админские колонки. На бэк не уходит.
     * Включается из роутера (props: { adminMode: true }) для админского реестра.
     * При встраивании в воронки/карточки не указывается — реестр работает в регулярном режиме.
     */
    adminMode: {
      type:    Boolean,
      default: false,
    },

    /**
     * Состояние позиций. id одного из PreordersService.States.
     * Маппится в бэк-флаг (isFinished / isDeleted).
     * Без значения — все состояния.
     */
    state: {
      type:    String as PropType<typeof PreordersService.States[number]['id']>,
      default: null,
    },

    /**
     * Фильтры реестра. Передаются страницей, на которой реестр смонтирован
     * (воронка собирает их из своего фильтра). Без них — без внешних фильтров.
     */
    params: {
      type:    Object as PropType<Parameters<typeof PreordersService.getListPaginated>[0]>,
      default: null,
    },

    /** Фильтр страницы (справа сверху), синхронный с фильтрами колонок — см. useExternalFilter */
    filter: {
      type:    Object as PropType<OrdersPageFilter | UnwrapNestedRefs<OrdersPageFilter>>,
      default: null,
    },

    showActionBar: {
      type:    Boolean,
      default: false,
    },

    hiddenColumns: {
      type:    Array as PropType<string[]>,
      default: () => [],
    },
  },

  setup() {
    return {
      PreordersService,
    };
  },

  data() {
    return {
      dataLoader: new DataGridLoader(PreordersService.getListPaginated)
          .useExtraParams({ requireCommentsInfo: true })
          .useReactiveSource(() => {
            return {
              ...this.params,
              ...PreordersService.getStatusFilter(this.state),
            };
          })
          /** Фильтр страницы синхронен с фильтрами колонок (грузоотправитель) — см. useExternalFilter */
          .useExternalFilter(() => this.filter as OrdersPageFilter | null),
      popup: {
        target: null,
        entity: null as Order | null,
        isSaveDisabled: false,
        isLoading: false,
      }
    };
  },

  computed: {
    /**
     * Админский режим — только если включён пропсом (роутер) и зритель действительно админ.
     */
    isAdminMode(): boolean {
      return this.adminMode && this.$core.currentUser.isAdmin;
    },

    /** Админ видит обе колонки; обычный продавец свою колонку не видит */
    showSellerColumn(): boolean {
      return this.isAdminMode || this.$core.currentPartner.isSeller === false;
    },

    /** Симметрично — обычный покупатель свою колонку не видит */
    showBuyerColumn(): boolean {
      return this.isAdminMode || this.$core.currentPartner.isBuyer === false;
    },

    /** Право видеть данные стороны продавца */
    canViewSellerData(): boolean {
      if (this.isAdminMode === true) return true;
      if (this.$core.currentPartner.isSeller === true) return true;
      if (this.$core.currentPartner.isLSZ === true) return true;

      return false;
    },

    /** Право видеть данные стороны покупателя. ЛСЗ сюда не попадает — он смотрит цену продавца */
    canViewBuyerData(): boolean {
      if (this.isAdminMode === true) return true;
      if (this.$core.currentPartner.isBuyer === true && this.$core.currentPartner.isLSZ !== true) return true;

      return false;
    },

    hiddenColumnsSet(): Set<string> {
      return new Set(this.hiddenColumns);
    },
  },

  methods: {
    closeBuyPopup() {
      this.popup.target = null;
      this.popup.entity = null;
      this.popup.isSaveDisabled = false;
    },

    /**
     * FIXME конструкторы отсюда переезжают в BuyOneClickPopup: попапу нужно
     * отдавать саму позицию предзаказа, а заказ из неё он соберёт сам.
     */
    openBuyPopup(event: Event, preorderPosition: PreorderPosition): void {
      this.popup.entity = Order.fromPreorderPosition(preorderPosition);
      this.popup.target = event.target;
    },

    async openPositionCard(row: PreorderPosition): Promise<void> {
      const submitted = await OrdersService.showOrderPositionCard(row);

      if (submitted === true) {
        await this.dataLoader.refresh();
      }
    },

    /** Быстрый заказ по позиции — то же, что пункт «Создать заказ» в контекстном меню */
    async createOrder(): Promise<void> {
      this.popup.isLoading = true;
      const success = await OrdersService.add(this.popup.entity);
      this.popup.isLoading = false;

      if (success === true) {
        this.closeBuyPopup();
        await this.dataLoader.refresh();
      }
    },

    async createPrintingPreorder(): Promise<void> {
      if (await OrdersService.showOrderCard(null, 'printing', null, true) === true) {
        await this.dataLoader.refresh();
      }
    },

    async createRawMaterialsPreorder(): Promise<void> {
      if (await OrdersService.showOrderCard(null, 'rawMaterialsAndPackage', null, true) === true) {
        await this.dataLoader.refresh();
      }
    },

    isColumnVisible(columnId: string): boolean {
      return this.hiddenColumnsSet.has(columnId) === false;
    },
  },
});
</script>
